import { useMutation, useQueryClient } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { useClient, type OrpcClient } from './useClient'
import { faxDetailQueryKey, faxInboxQueryKey } from './useFaxInbox'
import { faxPatientsQueryKey } from './useFaxPatients'

// The split plan the middleware files from (everything but the fax id).
export type FaxSplitPlan = Omit<Parameters<OrpcClient['triageIncomingFaxSplit']>[0], 'id'>

export function useFaxSplit(taskId: string) {
  const client = useClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (plan: FaxSplitPlan) => client.triageIncomingFaxSplit({ id: taskId, ...plan }),
    onSuccess: (result) => {
      const n = result.documents.length
      showNotification(
        n === 0
          ? // Every document was taken off its chart before "Finish filing".
            {
              color: 'blue',
              title: 'Fax split',
              message: 'Nothing was filed: every document had been taken off its chart.',
            }
          : {
              color: 'green',
              title: 'Fax split',
              message: `Filed to ${n} chart${n === 1 ? '' : 's'}.`,
            },
      )
    },
    onError: (error) => {
      showNotification({
        color: 'red',
        title: 'Error',
        message: error.message || 'Failed to split the fax.',
      })
    },
    // On a failure too: the server may hold the claim of a split that stopped part-way, and the
    // refetch shows it read-only, to be finished, instead of a form that can no longer be filed.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: faxInboxQueryKey({}) }),
        queryClient.invalidateQueries({ queryKey: faxDetailQueryKey(taskId) }),
        queryClient.invalidateQueries({ queryKey: faxPatientsQueryKey(taskId) }),
      ]),
  })
}
